import React, { useState, useEffect } from 'react';
import { getOpeningHours, updateOpeningHours, createOpeningHour, getBlockedDates, createBlockedDate, deleteBlockedDate } from '../services/pocketbase';
import { FiSave, FiClock, FiCalendar, FiTrash2, FiPlus, FiX, FiRefreshCw } from 'react-icons/fi';
import './AdminHours.css';

function AdminHours() {
    // State Horaires
    const [hours, setHours] = useState([]);
    const [loadingHours, setLoadingHours] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    // State Dates Bloquées
    const [blockedDates, setBlockedDates] = useState([]);
    const [loadingBlocked, setLoadingBlocked] = useState(true);
    const [blockedError, setBlockedError] = useState(null);
    const [newBlockedDate, setNewBlockedDate] = useState({ date: '', endDate: '', reason: '', isRange: false });
    const [isAddingBlocked, setIsAddingBlocked] = useState(false);

    const daysOfWeek = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoadingHours(true);
        setLoadingBlocked(true);

        // Charger Horaires
        try {
            const data = await getOpeningHours();
            // Tri par 'day' (1=Lundi, 7=Dimanche)
            const sorted = data.sort((a, b) => a.day - b.day);
            setHours(sorted);
        } catch (error) {
            console.error('Erreur horaires:', error);
            setMessage('Erreur chargement horaires');
        } finally {
            setLoadingHours(false);
        }

        // Charger Dates Bloquées
        try {
            const blockedData = await getBlockedDates();
            setBlockedDates(blockedData);
            setBlockedError(null);
        } catch (error) {
            console.error('Erreur dates bloquées:', error);
            // Vérifier si c'est une erreur 404 (Collection non trouvée)
            if (error.status === 404) {
                setBlockedError("Collection manquante");
            }
        } finally {
            setLoadingBlocked(false);
        }
    };

    // --- Logique Horaires ---

    const handleInitHours = async () => {
        setSaving(true);
        try {
            // Créer les 7 jours de la semaine par défaut
            const promises = [];
            for (let i = 1; i <= 7; i++) {
                promises.push(createOpeningHour({
                    day: i,
                    open: '09:00',
                    close: '18:00',
                    break_start: '12:00',
                    break_end: '13:00',
                    is_closed: i === 7 // Dimanche fermé par défaut
                }));
            }
            await Promise.all(promises);
            setMessage('Horaires initialisés avec succès !');
            loadData(); // Recharger pour afficher
        } catch (error) {
            console.error(error);
            setMessage("Erreur lors de l'initialisation des horaires");
        } finally {
            setSaving(false);
        }
    };

    const handleChangeHour = (id, field, value) => {
        setHours(hours.map(h =>
            h.id === id ? { ...h, [field]: value } : h
        ));
    };

    const handleSaveHour = async (id) => {
        setSaving(true);
        try {
            const hourToUpdate = hours.find(h => h.id === id);
            await updateOpeningHours(id, hourToUpdate);
            setMessage('Horaires mis à jour !');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error(error);
            setMessage('Erreur sauvegarde horaires');
        } finally {
            setSaving(false);
        }
    };

    // --- Logique Dates Bloquées ---

    const handleAddBlocked = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const start = new Date(newBlockedDate.date);
            const reason = newBlockedDate.reason;

            if (newBlockedDate.isRange && newBlockedDate.endDate) {
                const end = new Date(newBlockedDate.endDate);
                const datesToAdd = [];

                // Boucle pour générer toutes les dates
                for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                    datesToAdd.push(new Date(d));
                }

                // Création parallèle
                const promises = datesToAdd.map(dateObj => createBlockedDate({
                    date: dateObj.toISOString(),
                    reason: reason + (datesToAdd.length > 1 ? ` (Période)` : '')
                }));

                await Promise.all(promises);
                setMessage(`${datesToAdd.length} jours de congés ajoutés !`);
            } else {
                // Date simple
                await createBlockedDate({
                    date: start.toISOString(),
                    reason: reason
                });
                setMessage('Congé ajouté !');
            }

            // Rafraîchir la liste complète
            const updated = await getBlockedDates();
            setBlockedDates(updated);

            setNewBlockedDate({ date: '', endDate: '', reason: '', isRange: false });
            setIsAddingBlocked(false);
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error(error);
            setMessage('Erreur lors de l\'ajout du congé');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteBlocked = async (id) => {
        if (!window.confirm('Supprimer cette date bloquée ?')) return;
        try {
            await deleteBlockedDate(id);
            setBlockedDates(blockedDates.filter(d => d.id !== id));
            setMessage('Congé supprimé');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error(error);
            setMessage('Erreur suppression');
        }
    };

    if (loadingHours) return <div className="loader">Chargement...</div>;

    return (
        <div className="admin-hours">
            <div className="admin-header">
                <h2>🕒 Gestion des Horaires & Congés</h2>
            </div>

            {message && <div className={`message-banner ${message.includes('Erreur') ? 'error' : 'success'}`}>{message}</div>}

            {/* Section Horaires Hebdomadaires */}
            <h3 className="section-subtitle">Horaires d'ouverture hebdomadaires</h3>

            {hours.length === 0 ? (
                <div className="empty-state-hours">
                    <p>Aucun horaire configuré pour le moment.</p>
                    <button className="btn-init-hours" onClick={handleInitHours} disabled={saving}>
                        <FiRefreshCw className={saving ? 'spin' : ''} /> Initialiser les horaires (Lundi - Dimanche)
                    </button>
                </div>
            ) : (
                <div className="hours-grid">
                    {hours.map((dayHour) => (
                        <div key={dayHour.id} className={`hour-card ${dayHour.is_closed ? 'closed' : ''}`}>
                            <div className="hour-card-header">
                                <h3>{daysOfWeek[dayHour.day - 1] || 'Jour Inconnu'}</h3>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={!dayHour.is_closed}
                                        onChange={(e) => handleChangeHour(dayHour.id, 'is_closed', !e.target.checked)}
                                    />
                                    <span className="slider"></span>
                                </label>
                            </div>

                            {!dayHour.is_closed && (
                                <div className="hour-card-body">
                                    <div className="time-group">
                                        <label><FiClock /> Ouverture</label>
                                        <div className="time-inputs">
                                            <input
                                                type="time"
                                                value={dayHour.open}
                                                onChange={(e) => handleChangeHour(dayHour.id, 'open', e.target.value)}
                                            />
                                            <span>à</span>
                                            <input
                                                type="time"
                                                value={dayHour.close}
                                                onChange={(e) => handleChangeHour(dayHour.id, 'close', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="time-group">
                                        <label><FiClock /> Pause (facultatif)</label>
                                        <div className="time-inputs">
                                            <input
                                                type="time"
                                                value={dayHour.break_start || ''}
                                                onChange={(e) => handleChangeHour(dayHour.id, 'break_start', e.target.value)}
                                            />
                                            <span>à</span>
                                            <input
                                                type="time"
                                                value={dayHour.break_end || ''}
                                                onChange={(e) => handleChangeHour(dayHour.id, 'break_end', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="hour-card-footer">
                                <button className="btn-save-sm" onClick={() => handleSaveHour(dayHour.id)} disabled={saving}>
                                    <FiSave /> Enregistrer
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Section Dates Bloquées (Vacances) */}
            <div className="blocked-dates-section">
                <div className="blocked-header">
                    <h3><FiCalendar /> Congés & Fermetures Exceptionnelles</h3>
                    {!blockedError && (
                        <button className="btn-add-date" onClick={() => setIsAddingBlocked(!isAddingBlocked)}>
                            {isAddingBlocked ? <FiX /> : <FiPlus />} {isAddingBlocked ? 'Annuler' : 'Ajouter une date'}
                        </button>
                    )}
                </div>

                {blockedError ? (
                    <div className="setup-instructions-card">
                        <h4>⚠️ Configuration Requise</h4>
                        <p>Pour gérer vos congés, créez la collection suivante dans PocketBase :</p>
                        <ul>
                            <li><strong>Nom :</strong> <code>blocked_dates</code></li>
                            <li><strong>Champs :</strong>
                                <ul>
                                    <li><code>date</code> (Date)</li>
                                    <li><code>reason</code> (Texte)</li>
                                </ul>
                            </li>
                        </ul>
                        <button className="btn-retry-sm" onClick={loadData}>Réessayer</button>
                    </div>
                ) : (
                    <>
                        {isAddingBlocked && (
                            <form className="add-blocked-form" onSubmit={handleAddBlocked}>
                                <div className="form-group checkbox-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={newBlockedDate.isRange}
                                            onChange={e => setNewBlockedDate({ ...newBlockedDate, isRange: e.target.checked })}
                                        />
                                        Sélectionner une période (plusieurs jours)
                                    </label>
                                </div>

                                <div className="dates-row" style={{ display: 'flex', gap: '1rem' }}>
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label>{newBlockedDate.isRange ? 'Date de début' : 'Date'}</label>
                                        <input
                                            type="date"
                                            required
                                            value={newBlockedDate.date}
                                            onChange={e => setNewBlockedDate({ ...newBlockedDate, date: e.target.value })}
                                        />
                                    </div>

                                    {newBlockedDate.isRange && (
                                        <div className="form-group" style={{ flex: 1 }}>
                                            <label>Date de fin</label>
                                            <input
                                                type="date"
                                                required={newBlockedDate.isRange}
                                                value={newBlockedDate.endDate}
                                                min={newBlockedDate.date}
                                                onChange={e => setNewBlockedDate({ ...newBlockedDate, endDate: e.target.value })}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Raison (ex: Vacances, Férié)</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="Ex: Fermeture exceptionnelle"
                                        value={newBlockedDate.reason}
                                        onChange={e => setNewBlockedDate({ ...newBlockedDate, reason: e.target.value })}
                                    />
                                </div>
                                <button type="submit" className="btn-save-blocked" disabled={saving}>
                                    {saving ? 'Ajout en cours...' : 'Ajouter'}
                                </button>
                            </form>
                        )}

                        <div className="blocked-dates-list">
                            {blockedDates.length === 0 ? (
                                <p className="no-dates">Aucune date bloquée pour le moment.</p>
                            ) : (
                                blockedDates.map(dateItems => (
                                    <div key={dateItems.id} className="blocked-date-item">
                                        <div className="date-info">
                                            <span className="date-display">{new Date(dateItems.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                            <span className="date-reason">{dateItems.reason}</span>
                                        </div>
                                        <button className="btn-delete-date" onClick={() => handleDeleteBlocked(dateItems.id)}>
                                            <FiTrash2 />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default AdminHours;
